import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE, saveSessionToken, saveCachedUser } from '@/lib/api';
import { readStoredRef, clearStoredRef } from '@/lib/referral';

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

      if (!sessionId) {
        setError('No session found. Please try signing in again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        const ref = readStoredRef();
        const refQs = ref ? `&ref=${encodeURIComponent(ref)}` : '';
        const res = await fetch(`${API_BASE}/auth/session?session_id=${sessionId}${refQs}`, {
          credentials: 'include',
        });
        if (!res.ok) {
          let errText = '';
          try { errText = await res.text(); } catch (e) { /* body may have been consumed */ }
          throw new Error(errText || `Authentication failed (status ${res.status}). Please try again.`);
        }
        const userData = await res.json();
        if (ref) clearStoredRef();
        // Persist the session_token in localStorage as a resilient fallback
        // for production custom domains where Set-Cookie headers can be
        // dropped by edge proxies. Backend accepts both cookie + Bearer token.
        if (userData?.session_token) saveSessionToken(userData.session_token);
        // Cache the user object too — lets AuthContext hydrate optimistically
        // on subsequent page loads without a flash of /login while /auth/me
        // is in flight.
        saveCachedUser(userData);
        setUser(userData);

        // Belt and braces: re-read the canonical /auth/me so the context can
        // never be left holding a partial user. A partial user (no board, no
        // class) is what used to leave the header pickers blank until the
        // student manually refreshed.
        try { await checkAuth(); } catch { /* the session user above still stands */ }

        // CRITICAL: yield to the event loop so localStorage writes flush AND
        // React commits the setUser state update BEFORE the navigation
        // re-renders ProtectedRoute. Without this, on slow networks or
        // mid-spec mobile devices, the next render fires `apiGet('/auth/me')`
        // before the Bearer token is readable from localStorage → 401 →
        // user kicked back to /login. This was the production "2-second
        // kick" bug on custom domains where the cookie also gets stripped.
        await new Promise(r => setTimeout(r, 50));

        navigate('/home', { state: { user: userData }, replace: true });
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message || 'Something went wrong during sign-in. Please try again.');
        setTimeout(() => navigate('/login'), 4000);
      }
    };

    processAuth();
  }, [navigate, setUser, checkAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="auth-callback-error">
        <div className="text-center animate-slide-up max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-red-50 mx-auto mb-4 flex items-center justify-center">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          </div>
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">Sign-in Issue</h2>
          <p className="text-sm text-slate-500 font-body mb-4">{error}</p>
          <p className="text-xs text-slate-400 font-body">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback">
      <div className="text-center animate-slide-up">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600 font-body">Signing you in...</p>
      </div>
    </div>
  );
}
